import React, { useCallback, useContext, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import uniq from "lodash/uniq";
import Popover from "components/common/Popover";
import { Menu } from "components/bootstrap";
import OnClickPopoverValueGroups from "views/components/visualizations/OnClickPopover/OnClickPopoverValueGroups";
import PopoverTitle from "views/components/visualizations/OnClickPopover/PopoverTitle";
import { ActionContext, AdditionalContext } from "views/logic/ActionContext";
import fieldTypeFor from "views/logic/fieldtypes/FieldTypeFor";
import ActionDropdown from "views/components/actions/ActionDropdown";
import TypeSpecificValue from "views/components/TypeSpecificValue";
import useFieldActions from "views/components/actions/useFieldActions";
import useQueryFieldTypes from "views/hooks/useQueryFieldTypes";
import useOverflowingComponents from "views/hooks/useOverflowingComponents";
import hasMultipleValueForActions from "views/components/visualizations/utils/hasMultipleValueForActions";
import { humanSeparator } from "views/Constants";
const DivContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xxs};
  `
);
const linkContext = (pt) => {
  if (pt.source && pt.target && typeof pt.source === "object" && typeof pt.target === "object") {
    return { source: pt.source, target: pt.target, value: pt.value };
  }
  const cd = pt.customdata;
  if ((cd == null ? void 0 : cd.source) && (cd == null ? void 0 : cd.target) && typeof cd.source === "object" && typeof cd.target === "object") {
    return { source: cd.source, target: cd.target, value: cd.value };
  }
  return null;
};
const nodeContext = (pt) => {
  const cd = pt.customdata;
  return cd && typeof cd.field === "string" ? cd : null;
};
const SankeyActions = ({
  selected,
  onBack = void 0,
  onActionRun
}) => {
  const actionContext = useContext(ActionContext);
  const { additionalHandlerArgs } = useFieldActions();
  const { overflowingComponents, setOverflowingComponents } = useOverflowingComponents();
  const type = fieldTypeFor(selected.field, actionContext.fieldTypes);
  const handlerArgs = {
    field: selected.field,
    type,
    value: selected.value,
    contexts: actionContext,
    ...additionalHandlerArgs
  };
  const isOrCombination = actionContext.valuePathOperator === "OR";
  const showCombinedHeader = hasMultipleValueForActions(actionContext) && !isOrCombination;
  let headerContent;
  if (showCombinedHeader) {
    headerContent = actionContext.valuePath.map((entry, index) => {
      const [entryField, entryValue] = Object.entries(entry)[0];
      return /* @__PURE__ */ React.createElement(React.Fragment, { key: `${entryField}-${String(entryValue)}` }, index > 0 && humanSeparator, /* @__PURE__ */ React.createElement(
        TypeSpecificValue,
        {
          field: entryField,
          value: entryValue,
          type: fieldTypeFor(entryField, actionContext.fieldTypes),
          truncate: true
        }
      ));
    });
  } else if (isOrCombination) {
    headerContent = /* @__PURE__ */ React.createElement(TypeSpecificValue, { field: selected.field, value: selected.value, type, truncate: true });
  } else {
    headerContent = /* @__PURE__ */ React.createElement(React.Fragment, null, selected.field, " = ", /* @__PURE__ */ React.createElement(TypeSpecificValue, { field: selected.field, value: selected.value, type, truncate: true }));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(PopoverTitle, { onBackClick: onBack != null ? onBack : false }, headerContent), /* @__PURE__ */ React.createElement(Menu, { opened: true }, /* @__PURE__ */ React.createElement(
    ActionDropdown,
    {
      handlerArgs,
      type: "value",
      onMenuToggle: onActionRun,
      overflowingComponents,
      setOverflowingComponents
    }
  )));
};
const collectGroupItems = (valueGroups) => {
  var _a, _b;
  return [
    ...(_a = valueGroups.rowPivotValues) != null ? _a : [],
    ...(_b = valueGroups.columnPivotValues) != null ? _b : [],
    ...valueGroups.metricValue ? [valueGroups.metricValue] : []
  ];
};
const SankeyOnClickPopover = ({ clickPoint, config, onPopoverClose }) => {
  var _a, _b;
  const pt = clickPoint;
  const valueGroups = useMemo(() => {
    var _a2, _b2, _c, _d, _e, _f, _g, _h;
    if (!pt) return { title: "" };
    const link = linkContext(pt);
    if (link) {
      const srcLabel = (_a2 = link.source.label) != null ? _a2 : "";
      const tgtLabel = (_b2 = link.target.label) != null ? _b2 : "";
      const linkTitle = srcLabel && tgtLabel ? `${srcLabel} \u2192 ${tgtLabel}` : "Connection";
      const srcCustom = link.source.customdata;
      const tgtCustom = link.target.customdata;
      const linkValue = link.value;
      const metric = (_c = config == null ? void 0 : config.series) == null ? void 0 : _c[0];
      return {
        title: linkTitle,
        rowPivotValues: srcCustom ? [
          {
            field: srcCustom.field,
            value: srcCustom.value,
            text: srcLabel || String(srcCustom.value),
            traceColor: null
          }
        ] : [],
        columnPivotValues: tgtCustom ? [
          {
            field: tgtCustom.field,
            value: tgtCustom.value,
            text: tgtLabel || String(tgtCustom.value),
            traceColor: null
          }
        ] : [],
        metricValue: metric && linkValue !== void 0 ? { field: metric.effectiveName, value: linkValue, text: String(linkValue), traceColor: null } : void 0
      };
    }
    const node = nodeContext(pt);
    if (!node) return { title: (_d = pt.label) != null ? _d : "" };
    const groupingFields = uniq(
      [...(_e = config == null ? void 0 : config.rowPivots) != null ? _e : [], ...(_f = config == null ? void 0 : config.columnPivots) != null ? _f : []].flatMap((pivot) => pivot.fields)
    );
    const queryAcrossGroupings = (config == null ? void 0 : config.visualization) === "network" && groupingFields.length > 1;
    return {
      title: (_g = pt.label) != null ? _g : String(node.value),
      rowPivotValues: [
        {
          field: node.field,
          value: node.value,
          text: (_h = pt.label) != null ? _h : String(node.value),
          traceColor: null
        }
      ],
      columnPivotValues: [],
      metricValue: void 0,
      nodeSelection: queryAcrossGroupings ? {
        field: node.field,
        value: node.value,
        contexts: {
          valuePath: groupingFields.map((groupingField) => ({ [groupingField]: node.value })),
          valuePathOperator: "OR"
        }
      } : null
    };
  }, [pt, config]);
  const items = useMemo(() => collectGroupItems(valueGroups), [valueGroups]);
  const skipValueSelection = items.length === 1;
  const [selected, setSelected] = useState(() => {
    var _a2;
    if (!skipValueSelection) return null;
    return (_a2 = valueGroups.nodeSelection) != null ? _a2 : { field: items[0].field, value: items[0].value, contexts: null };
  });
  const types = useQueryFieldTypes();
  const additionalContextValue = useMemo(
    () => {
      var _a2, _b2;
      return {
        valuePath: (_a2 = selected == null ? void 0 : selected.contexts) == null ? void 0 : _a2.valuePath,
        valuePathOperator: (_b2 = selected == null ? void 0 : selected.contexts) == null ? void 0 : _b2.valuePathOperator,
        fieldTypes: types
      };
    },
    [(_a = selected == null ? void 0 : selected.contexts) == null ? void 0 : _a.valuePath, (_b = selected == null ? void 0 : selected.contexts) == null ? void 0 : _b.valuePathOperator, types]
  );
  const onActionRun = useCallback(() => {
    onPopoverClose();
    setSelected(null);
  }, [onPopoverClose]);
  const onBack = useCallback(() => setSelected(null), []);
  if (!pt) return null;
  return /* @__PURE__ */ React.createElement(Popover.Dropdown, { title: selected ? null : valueGroups.title }, /* @__PURE__ */ React.createElement(DivContainer, null, selected ? /* @__PURE__ */ React.createElement(AdditionalContext.Provider, { value: additionalContextValue }, /* @__PURE__ */ React.createElement(
    SankeyActions,
    {
      selected,
      onBack: skipValueSelection ? void 0 : onBack,
      onActionRun
    }
  )) : /* @__PURE__ */ React.createElement(
    OnClickPopoverValueGroups,
    {
      columnPivotValues: valueGroups.columnPivotValues,
      metricValue: valueGroups.metricValue,
      rowPivotValues: valueGroups.rowPivotValues,
      setFieldData: setSelected,
      config
    }
  )));
};
export default SankeyOnClickPopover;
